import { useState } from 'react';
import { useApiResource } from '@/hooks/useApiResource';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Repeat, Flame, Check, Trash2 } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Chip, SectionTitle, EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FAB, FormDrawer, TextField, SelectField } from '@/components/FormControls';
import api from '@/lib/api';
import { HABIT_CATEGORIES, HABIT_FREQUENCIES, categoryChip } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const emptyHabit = { name: '', category: 'Health', frequency: 'Daily' };
const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function Habits() {
  const { t, tEnum } = useLang();
  const { data, loading, reload: load } = useApiResource(
    () => api.get('/habits').then((r) => r.data),
    { errorMessage: t('habits.loadError') }
  );
  const habits = data || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(emptyHabit);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const toggle = async (h) => {
    try {
      await api.post(`/habits/${h.id}/toggle`);
      if (!h.completedToday) toast.success(t('habits.doneToday', { name: h.name }));
      load();
    } catch (e) {
      toast.error(t('habits.updateError'));
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('habits.nameRequired')); return; }
    setSaving(true);
    try {
      await api.post('/habits', form);
      toast.success(t('habits.added'));
      setForm(emptyHabit);
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('habits.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/habits/${deleteId}`);
      toast.success(t('habits.removed'));
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(t('habits.deleteError'));
    }
  };

  if (loading) return <FullScreenLoader label={t('habits.loader')} />;

  const doneToday = habits.filter((h) => h.completedToday).length;
  const weekFill = (streak) => {
    const arr = [false, false, false, false, false, false, false];
    for (let i = 0; i < Math.min(streak, 7); i += 1) arr[6 - i] = true;
    return arr;
  };

  return (
    <AppLayout>
      <AppHeader title={t('habits.title')} subtitle={t('habits.subtitle', { done: doneToday, total: habits.length })} back />

      <div className="mt-4">
        <SectionTitle>{t('habits.today')}</SectionTitle>
        <div data-testid="habits-today-list" className="space-y-2">
          {habits.length === 0 ? (
            <EmptyState icon={Repeat} title={t('habits.emptyTitle')} subtitle={t('habits.emptySubtitle')} />
          ) : (
            habits.map((h, i) => (
              <motion.div
                key={h.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: i * 0.03 }}
                className="glass-card rounded-2xl p-3.5"
              >
                <div className="flex items-center gap-3">
                  <button
                    data-testid={`habit-toggle-${h.id}`}
                    onClick={() => toggle(h)}
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition-colors',
                      h.completedToday ? 'border-emerald-300 bg-emerald-400/20 text-emerald-300' : 'border-white/20 text-transparent hover:border-emerald-300'
                    )}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm font-medium', h.completedToday ? 'text-foreground/50 line-through' : 'text-foreground/90')}>{h.name}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <Chip className={categoryChip(h.category)}>{tEnum(h.category)}</Chip>
                      <span className="text-[11px] text-foreground/45">{tEnum(h.frequency)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-amber-300">
                    <Flame className="h-4 w-4" />
                    <span className="font-mono-plex text-sm font-semibold">{h.streak}</span>
                  </div>
                  <button onClick={() => setDeleteId(h.id)} className="ml-1 text-foreground/30 hover:text-rose-300">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between gap-1">
                  {weekFill(h.streak).map((filled, idx) => (
                    <div key={`${h.id}-day-${idx}`} className="flex flex-1 flex-col items-center gap-1">
                      <div className={cn('h-7 w-full rounded-md', filled ? 'bg-emerald-400/70' : 'bg-white/[0.06]')} />
                      <span className="text-[10px] text-foreground/40">{DAYS[idx]}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      <FAB onClick={() => setDrawerOpen(true)} testId="habits-add-fab" label={t('habits.addFab')} />

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={t('habits.drawerTitle')}
        onSubmit={save}
        loading={saving}
        submitLabel={t('habits.addHabit')}
        submitTestId="habit-save-button"
      >
        <TextField label={t('habits.name')} testId="habit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('habits.namePlaceholder')} />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label={t('habits.category')} testId="habit-category-select" value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} options={HABIT_CATEGORIES} />
          <SelectField label={t('habits.frequency')} testId="habit-frequency-select" value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} options={HABIT_FREQUENCIES} />
        </div>
      </FormDrawer>

      {deleteId ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-6" onClick={() => setDeleteId(null)}>
          <div className="w-full max-w-[420px] rounded-2xl border border-white/10 bg-[#0A1730] p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-foreground">{t('habits.confirmTitle')}</p>
            <p className="mt-1 text-xs text-foreground/55">{t('habits.confirmDesc')}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 rounded-xl border border-white/10 bg-white/[0.06] py-2.5 text-sm font-medium text-foreground">{t('common.cancel')}</button>
              <button data-testid="habit-delete-confirm" onClick={doDelete} className="flex-1 rounded-xl bg-rose-500 py-2.5 text-sm font-medium text-white">{t('common.remove')}</button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}
